import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { RoleDetail } from '../../features/team/domain/role.model';

/**
 * HTTP client for role catalog endpoints.
 *
 * Shared data-access layer — used by team feature (filters, chips)
 * and dashboard (role labels).
 *
 * Caches the role list with shareReplay(1) to prevent duplicate HTTP calls.
 * Cache is invalidated on logout so the next session gets fresh roles.
 */
@Injectable({ providedIn: 'root' })
export class RoleApi {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private cached$: Observable<RoleDetail[]> | null = null;

  /** Fetch all roles from GET /roles. Cached with shareReplay(1). */
  listRoles(): Observable<RoleDetail[]> {
    if (!this.cached$) {
      this.cached$ = this.http
        .get<{ items: RoleDetail[] }>(`${this.apiUrl}/roles`)
        .pipe(
          map((res) => res.items),
          shareReplay(1),
        );
    }
    return this.cached$;
  }

  /** Clear the cache so the next listRoles() call fetches fresh data. */
  invalidateCache(): void {
    this.cached$ = null;
  }
}
